import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { Ubicacion } from "../../../services/v2/prospects.service";

/**
 * Mandar la ubicación de una o varias academias como pin de mapa de WhatsApp (Esteban, 2026-10-06: "debería abrir un
 * modal para elegir multi-selección qué planteles… una location de Google Maps exactamente adonde está la academia").
 * La lista es propia (ct_ubicaciones), no sale de los planteles de WAPP. Superadmin la corrige pegando el link de Maps.
 */
export const mapaLink = (u: { lat: number; lng: number }) => `https://www.google.com/maps/search/?api=1&query=${u.lat},${u.lng}`;
const mapaEmbed = (u: { lat: number; lng: number }) => `https://maps.google.com/maps?q=${u.lat},${u.lng}&z=17&output=embed`;

/** Saca lat,lng de un link de Google Maps (…/@lat,lng,17z, !3dlat!4dlng, ?q=lat,lng) o de "lat, lng" escrito. */
export const coordsDeLink = (t: string): { lat: number; lng: number } | null => {
	const s = decodeURIComponent(t || "");
	const m = s.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/) || s.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) || s.match(/[?&](?:q|query|ll)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/) || s.match(/^\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*$/);
	if (!m) return null;
	const lat = Number(m[1]), lng = Number(m[2]);
	return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
};

const IcoPin = () => <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>;

interface Props {
	campusId: number;
	prospectId: number;
	/** Plantel de la persona: su academia sale marcada de entrada. */
	plantelId: number | null;
	canalId: number | null;
	puedeEnviar: boolean;
	onEnviado: () => void;
	onClose: () => void;
}

const UbicacionModal: React.FC<Props> = ({ campusId, prospectId, plantelId, canalId, puedeEnviar, onEnviado, onClose }) => {
	const http = HttpManager.getInstance();
	const [lista, setLista] = useState<Ubicacion[] | null>(null);
	const [puedeEditar, setPuedeEditar] = useState(false);
	const [marcadas, setMarcadas] = useState<number[]>([]);
	const [vista, setVista] = useState<number | null>(null);
	const [editando, setEditando] = useState<(Partial<Ubicacion> & { link?: string }) | null>(null);
	const [ocupado, setOcupado] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const cargar = (r: { puedeEditar: boolean; ubicaciones: Ubicacion[] }, primera = false) => {
		setLista(r.ubicaciones);
		setPuedeEditar(r.puedeEditar);
		if (primera) {
			const suya = r.ubicaciones.find((u) => plantelId && u.campus_id === plantelId);
			if (suya) { setMarcadas([suya.id]); setVista(suya.id); } else if (r.ubicaciones[0]) setVista(r.ubicaciones[0].id);
		}
	};
	useEffect(() => {
		http.prospectsService.ubicaciones(campusId).then((r) => cargar(r.data, true)).catch(() => setError("No se pudieron cargar las ubicaciones."));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId]);

	const alternar = (id: number) => { setMarcadas((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id])); setVista(id); };
	const enviar = async () => {
		setOcupado(true); setError(null);
		try { await http.prospectsService.enviarUbicaciones(campusId, prospectId, marcadas, canalId); onEnviado(); onClose(); }
		catch (e: any) { setError(e?.response?.data?.message || "No se pudo enviar."); }
		finally { setOcupado(false); }
	};
	const guardar = async (quitar = false) => {
		if (!editando) return;
		setOcupado(true); setError(null);
		try {
			const c = editando.link ? coordsDeLink(editando.link) : null;
			if (editando.link && !c) throw new Error("No encontré el punto en ese link. En Google Maps abre la academia y copia el link de la barra de direcciones.");
			const r = await http.prospectsService.guardarUbicacion(campusId, { id: editando.id, nombre: editando.nombre, direccion: editando.direccion,
				lat: c ? c.lat : editando.lat, lng: c ? c.lng : editando.lng, campus_id: editando.campus_id ?? null, quitar });
			cargar(r.data);
			setEditando(null);
		} catch (e: any) { setError(e?.response?.data?.message || e?.message || "No se pudo guardar."); }
		finally { setOcupado(false); }
	};

	const enVista = (lista || []).find((u) => u.id === vista) || null;
	const previa = editando?.link ? coordsDeLink(editando.link) : null;
	const elegidas = (lista || []).filter((u) => marcadas.includes(u.id));

	return (
		<Modal show onHide={onClose} size="lg" className="ventas-nuevo-modal ub-modal" fullscreen="sm-down">
			<Modal.Header closeButton>
				<Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>{editando ? (editando.id ? "Corregir ubicación" : "Agregar academia") : "Enviar ubicación"}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{error ? <div className="aviso error">{error}</div> : null}
				{!lista && !error ? <div className="apagado">Cargando…</div> : null}
				{editando ? (
					<div className="ub-editar">
						<label>Nombre<input value={editando.nombre || ""} maxLength={120} onChange={(e) => setEditando({ ...editando, nombre: e.target.value })} placeholder="Washington Academy Monterrey" /></label>
						<label>Dirección (la ve el cliente)<textarea rows={2} value={editando.direccion || ""} maxLength={300} onChange={(e) => setEditando({ ...editando, direccion: e.target.value })} /></label>
						<label>Link de Google Maps{editando.id ? " (solo si cambia el punto)" : ""}
							<input value={editando.link || ""} onChange={(e) => setEditando({ ...editando, link: e.target.value })} placeholder="https://www.google.com/maps/place/…" /></label>
						<div className="ub-ayuda">En Google Maps busca la academia, ábrela y copia el link de la barra de direcciones.</div>
						{previa || (editando.lat && editando.lng) ? (
							<iframe className="ub-mapa" title="Vista del mapa" loading="lazy" src={mapaEmbed(previa || { lat: Number(editando.lat), lng: Number(editando.lng) })} />
						) : null}
					</div>
				) : (
					<div className="ub-cuerpo">
						<div className="ub-lista">
							{(lista || []).map((u) => {
								const on = marcadas.includes(u.id);
								return (
									<div key={u.id} className={"ub-fila" + (on ? " on" : "") + (vista === u.id ? " vista" : "")}>
										<button type="button" className="ub-tocar" aria-pressed={on} onClick={() => alternar(u.id)}>
											<span className="hm-check" aria-hidden="true">{on ? "✓" : ""}</span>
											<span className="ub-tx"><b>{u.nombre}</b><span>{u.direccion}</span></span>
										</button>
										{puedeEditar ? <button type="button" className="ub-lapiz" onClick={() => setEditando({ ...u })} aria-label={"Corregir " + u.nombre}>Editar</button> : null}
									</div>
								);
							})}
							{puedeEditar ? <button type="button" className="ub-agregar" onClick={() => setEditando({ nombre: "", direccion: "", link: "" })}>+ Agregar academia</button> : null}
						</div>
						{enVista ? (
							<div className="ub-previa">
								<iframe className="ub-mapa" title={"Mapa de " + enVista.nombre} loading="lazy" src={mapaEmbed(enVista)} />
								<div className="ub-burbuja">
									<span className="ub-pin"><IcoPin /></span>
									<span className="ub-tx"><b>{enVista.nombre}</b><span>{enVista.direccion}</span></span>
								</div>
								<a className="ub-abrir" href={mapaLink(enVista)} target="_blank" rel="noreferrer">Ver en Google Maps</a>
								<div className="ub-ayuda">Así le llega: un mapa con el pin, el nombre y la dirección. Lo toca y abre su app de mapas.</div>
							</div>
						) : null}
					</div>
				)}
			</Modal.Body>
			<Modal.Footer className="hm-pie">
				{editando ? (
					<>
						{editando.id ? <button className="btn-wa" disabled={ocupado} onClick={() => guardar(true)} style={{ marginRight: "auto" }}>Quitar de la lista</button> : null}
						<button className="btn-wa" onClick={() => setEditando(null)}>Cancelar</button>
						<button className="btn-wa pri" disabled={ocupado} onClick={() => guardar()}>{ocupado ? "Guardando…" : "Guardar"}</button>
					</>
				) : (
					<button className="btn-wa pri hm-enviar" disabled={!elegidas.length || !puedeEnviar || ocupado} onClick={enviar}
						title={puedeEnviar ? "" : "Pasaron 24 h desde su último mensaje: solo plantilla"}>
						{ocupado ? "Enviando…" : elegidas.length > 1 ? "Enviar " + elegidas.length + " ubicaciones" : "Enviar ubicación"}
					</button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default UbicacionModal;
